<template>
  <RouteWithChildren :parent-level="3">
    <ItemDetail
      :key="explorerToggleOptions.length"
      ref="itemDetail"
      :item-title="shipment ? shipment.trackingRef : ''"
      :item="shipment || {}"
      :rows="rows"
      :is-loading="isLoading"
      :is-loading-in-background="isLoadingInBackground"
      :error="errorMessage"
      :explorer-toggle-options="explorerToggleOptions"
      @close="close"
    />
  </RouteWithChildren>
</template>
<script>
import RouteWithChildren from '../../components/organisms/layout/route-with-children';
import ItemDetail from '../../components/organisms/items/item-detail';

// exploreToggleOptions
import ShipmentTimeline from '../../components/organisms/shipments/shipment-timeline';
import ShipmentMap from '../../components/organisms/shipments/shipment-map';
import AssetsTable from '../../components/organisms/assets/assets-table';
import AssetsTableFiltered from '../../components/organisms/assets/assets-table-filtered';

import {
  routeNames,
  alertIcons,
  itemDetailTabSlugs,
  icons,
  assetFilterSelectedKeys,
  filterKeyToQueryStringParam,
  assetFilterKeys,
} from '../../constants';
import { shipments as demoShipments, facilities as demoFacilities, assets as demoAssets } from '../../data/shipments-demo';
import { constructEtaValue } from '../../helpers/shipment-helper';

export default {
  name: 'ShipmentDetail',
  components: {
    RouteWithChildren,
    ItemDetail,
  },
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      loadingDataForItemIds: [],
    };
  },
  computed: {
    isShipmentsDemo() { return this.$root.workspaceFeatures.shipmentsDemo; },
    isLoadingInBackground() { return this.loadingDataForItemIds.length > 0; },
    isLoading() {
      return !this.isLoadingInBackground && this.$store.state.shipments.isLoadingShipment;
    },
    itemAlreadyLoaded() { return !!this.shipment; },
    shipment() {
      if (this.isShipmentsDemo) {
        const shipment = demoShipments.find((s) => s.id === this.id) || {};
        return {
          ...shipment,
          origin: {
            ...shipment.origin,
            where: {
              ...shipment.origin.where,
              facility: demoFacilities
                .find((facility) => facility.id === shipment.origin.where.facility.id),
            },
          },
          destination: {
            ...shipment.destination,
            where: {
              ...shipment.destination.where,
              facility: demoFacilities
                .find((facility) => facility.id === shipment.destination.where.facility.id),
            },
          },
        };
      }

      return this.$store.state.shipments.shipments.find(({ id }) => id === this.id) || {};
    },
    errorMessage() { return this.isShipmentsDemo ? '' : this.$store.state.shipments.errorMessage; },
    explorerToggleOptions() {
      const tabs = [];

      if (this.isShipmentsDemo) {
        tabs.push({
          displayName: 'Timeline',
          slug: itemDetailTabSlugs.timeline,
          component: ShipmentTimeline,
          componentProps: {
            shipmentId: this.id,
          },
        });
      }

      tabs.push(...[
        {
          displayName: 'Map',
          slug: itemDetailTabSlugs.map,
          component: ShipmentMap,
          componentProps: {
            shipmentId: this.id,
          },
        },
      ]);

      if (this.isShipmentsDemo) {
        tabs.push({
          displayName: 'Assets',
          slug: itemDetailTabSlugs.assets,
          component: AssetsTable,
          componentProps: {
            smallerGapBottom: true,
            withTitle: false,
            displayHasLabel: false,
            noBorder: true,
            itemIds: demoAssets
              .filter((a) => a.consignmentId === this.shipment.id).map((a) => a.id),
          },
        });
      } else {
        tabs.push({
          displayName: 'Assets',
          slug: itemDetailTabSlugs.assets,
          component: AssetsTableFiltered,
          componentProps: {
            id: this.id,
            filters: [{ key: assetFilterSelectedKeys.consignments, value: this.id }],
            selectItemRoute: (d) => ({
              name: routeNames.assets.detail,
              params: { id: (d || {}).id },
              query: {
                [filterKeyToQueryStringParam[assetFilterKeys.consignments]]: this.id,
              },
            }),
            removeFiltersOnDestroy: false,
          },
        });
      }

      return tabs;
    },
    rows() {
      const rows = [
        { name: 'Tracking ref', value: this.shipment.trackingRef },
        { name: 'Asset count', value: this.shipment.pieceCount },
      ];

      // origin
      if (this.shipment.originFacilityId) {
        rows.push({
          name: 'Origin',
          value: {
            value: this.shipment.originName,
            icon: icons.facility,
            // iconColor: 'primary',
            // iconSize: '20',
          },
          buttons: [{
            props: {
              text: 'View Facility',
              isText: true,
              isSmall: true,
              onClick: () => this.$router.push({
                name: routeNames.facilities.detail,
                params: { id: this.shipment.originFacilityId },
              }),
            },
          }],
        });
      } else {
        rows.push({
          name: 'Origin',
          value: this.isShipmentsDemo
            ? this.shipment.origin.where.facility.name
            : this.shipment.originName,
        });
      }

      // destination
      if (this.shipment.destinationFacilityId) {
        rows.push({
          name: 'Destination',
          value: {
            value: this.shipment.destinationName,
            icon: icons.facility,
            // iconColor: 'warning',
            // iconSize: '20',
          },
          buttons: [{
            props: {
              text: 'View Facility',
              isText: true,
              isSmall: true,
              onClick: () => this.$router.push({
                name: routeNames.facilities.detail,
                params: { id: this.shipment.destinationFacilityId },
              }),
            },
          }],
        });
      } else {
        rows.push({
          name: 'Destination',
          value: this.isShipmentsDemo
            ? this.shipment.destination.where.facility.name
            : this.shipment.destinationName,
        });
      }

      if (this.isShipmentsDemo) {
        rows.push(...[
          {
            name: 'ETA',
            value: constructEtaValue(
              (this.shipment.destination.when || {}).estimated,
              this.shipment.destination,
            ),
          },
          {
            name: 'Status',
            value: {
              value: this.shipment.alerts.length > 0 ? this.shipment.alerts[0].message : 'OK',
              icon: this.shipment.alerts.length > 0 ? alertIcons[this.shipment.alerts[0].category][this.shipment.alerts[0].type] : '',
              iconColor: 'warning',
            },
          },
        ]);
      }

      return rows;
    },
  },
  watch: {
    id() { this.loadData(); },
  },
  created() {
    // Redirect to home if shipments is disabled
    if (!this.$root.workspaceFeatures.shipmentsDemo
     && !this.$root.workspaceFeatures.shipmentScreens) {
      this.$router.push({ name: routeNames.home });
      return;
    }
    this.loadData();
  },
  methods: {
    close() {
      this.$router.push({ name: routeNames.shipments.list, query: this.$route.query });
    },
    async loadData() {
      if (this.itemAlreadyLoaded) this.loadingDataForItemIds.push(this.id);
      const shipmentData = await this.$store.dispatch('shipments/getShipment', this.id);
      this.loadingDataForItemIds = this.loadingDataForItemIds
        .filter((id) => shipmentData && id !== shipmentData.id);
    },
  },
};
</script>
